import { ChevronRight, ChevronDown, Folder, Search } from 'lucide-react'
import { useState, useMemo } from 'react'

import { Input } from '@/components/ui/input'
import type { RecordGroup } from '@/types/api/record-group'

interface RecordGroupTreePickerProps {
  recordGroups: RecordGroup[]
  selectedId: string | null
  onSelect: (id: string) => void
  placeholder?: string
}

interface TreeItem {
  id: string
  name: string
  parentRecordGroupId: string | null
  children: TreeItem[]
}

export function RecordGroupTreePicker({
  recordGroups,
  selectedId,
  onSelect,
  placeholder = 'Search record groups...',
}: RecordGroupTreePickerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

  // Toggle node expansion
  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Build tree hierarchy
  const treeData = useMemo(() => {
    const itemMap = new Map<string, TreeItem>()
    const roots: TreeItem[] = []

    // Initialize map
    recordGroups.forEach((group) => {
      itemMap.set(group.id, {
        id: group.id,
        name: group.name,
        parentRecordGroupId: group.parentRecordGroupId,
        children: [],
      })
    })

    // Construct hierarchy
    itemMap.forEach((item) => {
      if (item.parentRecordGroupId && itemMap.has(item.parentRecordGroupId)) {
        itemMap.get(item.parentRecordGroupId)!.children.push(item)
      } else {
        roots.push(item)
      }
    })

    return roots;
  }, [recordGroups])

  // Filter tree based on search query
  const filteredTreeData = useMemo(() => {
    if (!searchQuery) return treeData

    const lowerQuery = searchQuery.toLowerCase()

    const filterNodes = (nodes: TreeItem[]): TreeItem[] => {
      return nodes
        .map((node) => {
          const childrenFiltered = filterNodes(node.children)
          const matchesSearch = node.name.toLowerCase().includes(lowerQuery)

          if (matchesSearch || childrenFiltered.length > 0) {
            // Auto-expand nodes that match or have matching children
            setExpandedNodes((prev) => {
              const next = new Set(prev)
              next.add(node.id)
              return next
            })
            return {
              ...node,
              children: childrenFiltered,
            }
          }
          return null
        })
        .filter((node): node is TreeItem => node !== null)
    }

    return filterNodes(treeData)
  }, [treeData, searchQuery])

  // Render individual node recursively
  const renderNode = (node: TreeItem, depth: number = 0) => {
    const hasChildren = node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    const isSelected = selectedId === node.id

    return (
      <div key={node.id} className="space-y-1 select-none">
        <div
          onClick={() => onSelect(node.id)}
          style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
          className={`flex items-center gap-2 rounded-lg py-2 px-3 cursor-pointer transition-all duration-200 border
            ${
              isSelected
                ? 'bg-primary-50 text-primary-700 font-semibold border-primary-200 shadow-sm'
                : 'bg-white hover:bg-neutral-50 text-neutral-700 border-neutral-100'
            }`}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => toggleExpand(node.id, e)}
              className="p-0.5 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <Folder
            className={`h-4 w-4 shrink-0 transition-colors ${
              isSelected ? 'text-primary-500' : 'text-neutral-400'
            }`}
          />
          <span className="text-xs truncate leading-none">{node.name}</span>
        </div>

        {hasChildren && isExpanded && (
          <div className="space-y-1">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          className="pl-9 h-9 text-xs"
        />
      </div>

      <div className="max-h-60 overflow-y-auto border border-neutral-200 rounded-lg p-2 bg-neutral-50/30 space-y-1">
        {filteredTreeData.length > 0 ? (
          filteredTreeData.map((node) => renderNode(node, 0))
        ) : (
          <div className="text-center py-6 text-xs text-neutral-400 font-medium">
            No record groups found.
          </div>
        )}
      </div>
    </div>
  )
}
